// URL 파라미터 ↔ 카드 상태 변환 공통 유틸

export const keyMap = {
  name: 'n', title: 't', company: 'c', email: 'em', phone: 'p', website: 'w', sns: 's', address: 'a', logo: 'lg',
  videoUrl: 'vu', engName: 'en', tagline: 'tg', videoOverlay: 'vo',
  show_title: 'st', show_company: 'sc', show_email: 'se', show_phone: 'sp', show_website: 'sw', show_sns: 'ss', show_address: 'sa',
  layout: 'ly', font: 'ft', emphasis: 'ep', verticalAlign: 'va', bgColor: 'bg',
  qrDotsType: 'qdt', qrDotsColor: 'qdc', qrBgColor: 'qbc',
  step: 'v', complete: 'x', showLogo: 'sl',
};
export const reverseKeyMap = Object.fromEntries(Object.entries(keyMap).map(([k, v]) => [v, k]));

export const reverseValueMap = {
  ly: { h1: 'h-layout1', h2: 'h-layout2', h3: 'h-layout3', v1: 'v-layout1' },
  ft: { p: 'Pretendard', n: 'Noto Sans KR', m: 'Montserrat', d: 'Playfair Display' },
  ep: { n: 'name', c: 'company' },
  va: { c: 'center', t: 'top' },
};

export function decompressData(base64String) {
  try {
    const compressed = window.atob(base64String).split('').map(c => c.charCodeAt(0));
    const decompressed = window.pako.inflate(new Uint8Array(compressed));
    return new TextDecoder().decode(decompressed);
  } catch {
    return null;
  }
}

export function compressData(string) {
  try {
    const data = new TextEncoder().encode(string);
    const compressed = window.pako.deflate(data);
    return window.btoa(String.fromCharCode.apply(null, compressed));
  } catch {
    return null;
  }
}

const defaultCardData = {
  details: { name: '', title: '', company: '', email: '', phone: '', website: '', sns: '', address: '', logo: '', videoUrl: '', engName: '', tagline: '', videoOverlay: '' },
  displayOptions: { title: true, company: true, email: true, phone: true, website: true, sns: false, address: false },
};

const valueMap = {
  'h-layout1': 'h1', 'h-layout2': 'h2', 'h-layout3': 'h3', 'v-layout1': 'v1',
  'Pretendard': 'p', 'Noto Sans KR': 'n', 'Montserrat': 'm', 'Playfair Display': 'd',
  'name': 'n', 'company': 'c',
  'center': 'c', 'top': 't',
};

// 카드 상태 객체 → URLSearchParams 문자열 (편집 URL 복원용)
export function serializeCardState(state) {
  const { cardData, layout, font, emphasis, verticalAlign, backgroundColor, showLogo, qrOptions } = state;
  const { details, displayOptions } = cardData;

  const params = new URLSearchParams();
  for (const key in details) {
    if (details[key] !== undefined && details[key] !== '') params.set(keyMap[key], details[key]);
  }
  for (const key in displayOptions) {
    params.set(keyMap[`show_${key}`], displayOptions[key]);
  }
  if (layout) params.set(keyMap.layout, valueMap[layout] || layout);
  if (font) params.set(keyMap.font, valueMap[font] || font);
  if (emphasis) params.set(keyMap.emphasis, valueMap[emphasis] || emphasis);
  if (verticalAlign) params.set(keyMap.verticalAlign, valueMap[verticalAlign] || verticalAlign);
  if (backgroundColor) params.set(keyMap.bgColor, backgroundColor);
  params.set(keyMap.showLogo, showLogo ?? true);
  if (qrOptions) {
    params.set(keyMap.qrDotsType, qrOptions.dotsOptions?.type || 'rounded');
    params.set(keyMap.qrDotsColor, qrOptions.dotsOptions?.color || '#000000');
    params.set(keyMap.qrBgColor, qrOptions.backgroundOptions?.color || '#ffffff');
  }

  const compressed = compressData(params.toString());
  return compressed ? `?d=${encodeURIComponent(compressed)}` : `?${params.toString()}`;
}

// URLSearchParams 문자열 → 카드 상태 객체
export function parseCardState(search) {
  let params = new URLSearchParams(search);

  const compressedParam = params.get('d');
  if (compressedParam) {
    const decompressed = decompressData(decodeURIComponent(compressedParam));
    if (decompressed) params = new URLSearchParams(decompressed);
  }

  const cardData = {
    details: { ...defaultCardData.details },
    displayOptions: { ...defaultCardData.displayOptions },
  };

  let layout = 'h-layout1';
  let font = 'Pretendard';
  let emphasis = 'name';
  let verticalAlign = 'center';
  let backgroundColor = '#ffffff';
  let showLogo = true;
  let qrOptions = { dotsOptions: { type: 'rounded', color: '#000000' }, backgroundOptions: { color: '#ffffff' } };

  params.forEach((value, key) => {
    const fullKey = reverseKeyMap[key];
    if (!fullKey) return;

    if (fullKey in cardData.details) {
      cardData.details[fullKey] = value;
    } else if (fullKey.startsWith('show_')) {
      const optionKey = fullKey.replace('show_', '');
      if (optionKey in cardData.displayOptions) {
        cardData.displayOptions[optionKey] = value === 'true';
      }
    }
  });

  if (params.get('ly')) layout = reverseValueMap.ly[params.get('ly')] || 'h-layout1';
  if (params.get('ft')) font = reverseValueMap.ft[params.get('ft')] || 'Pretendard';
  if (params.get('ep')) emphasis = reverseValueMap.ep[params.get('ep')] || 'name';
  if (params.get('va')) verticalAlign = reverseValueMap.va[params.get('va')] || 'center';
  if (params.get('bg')) backgroundColor = params.get('bg');
  if (params.get('sl')) showLogo = params.get('sl') === 'true';
  if (params.get('qdt')) qrOptions = { ...qrOptions, dotsOptions: { ...qrOptions.dotsOptions, type: params.get('qdt') } };
  if (params.get('qdc')) qrOptions = { ...qrOptions, dotsOptions: { ...qrOptions.dotsOptions, color: params.get('qdc') } };
  if (params.get('qbc')) qrOptions = { ...qrOptions, backgroundOptions: { color: params.get('qbc') } };

  const orientation = layout.startsWith('v') ? 'vertical' : 'horizontal';

  return { cardData, layout, orientation, font, emphasis, verticalAlign, backgroundColor, showLogo, qrOptions };
}
